import React, { useState, useEffect } from 'react';
import { X, Heart, MoreHorizontal, Send } from 'lucide-react';
import { Post, PostComment } from '../../types';
import { useAuthStore } from '../../store/authStore';
import { addComment, deleteComment, likeComment, unlikeComment } from '../../lib/engagementService';
import { supabase } from '../../lib/supabase';

interface CommentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  post: Post;
  onCommentAdded: () => void;
  onCommentDeleted: () => void;
}

export const CommentsModal: React.FC<CommentsModalProps> = ({
  isOpen,
  onClose,
  post,
  onCommentAdded,
  onCommentDeleted
}) => {
  const { user: currentUser } = useAuthStore();
  const [comments, setComments] = useState<PostComment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadComments();
    }
  }, [isOpen, post.id]);

  const loadComments = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('post_comments')
        .select(`
          *,
          profiles:user_id (
            id,
            username,
            full_name,
            avatar_url
          ),
          comment_likes (
            id,
            user_id
          )
        `)
        .eq('post_id', post.id)
        .order('created_at', { ascending: true });

      if (error) throw error;

      const transformedComments = (data || []).map(comment => ({
        id: comment.id,
        user_id: comment.user_id,
        user: {
          id: comment.profiles.id,
          username: comment.profiles.username,
          displayName: comment.profiles.full_name || comment.profiles.username,
          profileImage: comment.profiles.avatar_url || 'https://images.pexels.com/photos/2253275/pexels-photo-2253275.jpeg?auto=compress&cs=tinysrgb&w=300',
          bio: '',
          followerCount: 0,
          followingCount: 0,
          postCount: 0,
        },
        content: comment.content,
        created_at: comment.created_at,
        updated_at: comment.updated_at,
        like_count: comment.comment_likes.length,
        is_liked: comment.comment_likes.some((like: any) => like.user_id === currentUser?.id),
      }));

      setComments(transformedComments);
    } catch (error) {
      console.error('Error loading comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || !newComment.trim() || submitting) return;

    setSubmitting(true);
    try {
      await addComment(post.id, currentUser.id, newComment.trim());
      setNewComment('');
      onCommentAdded();
      await loadComments(); // Reload comments to show the new one
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Failed to add comment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!currentUser) return;

    try {
      await deleteComment(commentId, currentUser.id);
      setComments(prev => prev.filter(comment => comment.id !== commentId));
      onCommentDeleted();
    } catch (error) {
      console.error('Error deleting comment:', error);
      alert('Failed to delete comment. Please try again.');
    }
  };

  const handleLikeComment = async (commentId: string, isLiked: boolean) => {
    if (!currentUser) return;

    try {
      if (isLiked) {
        await unlikeComment(commentId, currentUser.id);
      } else {
        await likeComment(commentId, currentUser.id);
      }

      // Update local state
      setComments(prev => prev.map(comment => 
        comment.id === commentId 
          ? {
              ...comment,
              is_liked: !isLiked,
              like_count: isLiked ? comment.like_count - 1 : comment.like_count + 1
            }
          : comment
      ));
    } catch (error) {
      console.error('Error toggling comment like:', error);
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return `${diffInSeconds}s`;
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h`;
    return `${Math.floor(diffInSeconds / 86400)}d`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Comments</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Comments List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
            </div>
          ) : comments.length > 0 ? (
            comments.map((comment) => (
              <div key={comment.id} className="flex space-x-3">
                <img
                  src={comment.user.profileImage}
                  alt={comment.user.username}
                  className="h-8 w-8 rounded-full object-cover flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-sm">
                        <span className="font-medium mr-2">{comment.user.username}</span>
                        {comment.content}
                      </p>
                      <div className="flex items-center space-x-4 mt-1">
                        <span className="text-xs text-gray-500">
                          {formatTimeAgo(comment.created_at)}
                        </span>
                        {comment.like_count > 0 && (
                          <span className="text-xs text-gray-500">
                            {comment.like_count} likes
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 ml-2">
                      <button
                        onClick={() => handleLikeComment(comment.id, comment.is_liked)}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <Heart
                          className={`h-4 w-4 ${
                            comment.is_liked ? 'fill-red-500 text-red-500' : ''
                          }`}
                        />
                      </button>
                      {currentUser?.id === comment.user_id && (
                        <button
                          onClick={() => handleDeleteComment(comment.id)}
                          className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>No comments yet.</p>
              <p className="text-sm">Be the first to comment!</p>
            </div>
          )}
        </div>

        {/* Comment Input */}
        <form onSubmit={handleSubmitComment} className="border-t border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <img
              src={currentUser?.user_metadata?.avatar_url || 'https://images.pexels.com/photos/2253275/pexels-photo-2253275.jpeg?auto=compress&cs=tinysrgb&w=300'}
              alt="Your avatar"
              className="h-8 w-8 rounded-full object-cover flex-shrink-0"
            />
            <div className="flex-1 flex items-center space-x-2">
              <input
                type="text"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment..."
                className="flex-1 text-sm border-0 focus:ring-0 outline-none bg-gray-50 rounded-full px-4 py-2"
                disabled={submitting}
              />
              <button
                type="submit"
                disabled={!newComment.trim() || submitting}
                className="text-purple-600 font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};